/* ============================================================================
 * 会话状态：当前角色 / RBAC
 * V2 的 RBAC 是实测强项（导航按权限过滤，零个"点进去才发现无权"的死路），
 * V3 继承该行为，但**角色来源换了**（A4）：
 *   以前：localStorage 里的一个字符串，用户自己选，服务端不校验
 *   现在：服务端登录令牌里的 role，前端只读不写
 * 于是这里只剩「按角色决定显示什么」，「能不能做」由服务端说了算。
 * ========================================================================== */
import { create } from 'zustand';
import { ROLES, type RoleCode } from '@/domain/master';
import { useAuth } from './auth';

const FALLBACK: RoleCode = 'OPS';

function isRole(v: string | undefined): v is RoleCode {
  return !!v && v in ROLES;
}

interface SessionState {
  /** 路由是否对当前角色可见。导航渲染与路由守卫必须走同一个判断，否则会出现死路。 */
  navAllowed: (route: string) => boolean;
  /** 前端可见性判断。⚠ 它只决定「显不显示」——真正的准入在服务端。 */
  can: (key: string) => boolean;
  role: () => RoleCode;
}

function currentRole(): RoleCode {
  const r = useAuth.getState().user?.role;
  return isRole(r) ? r : FALLBACK;
}

export const useSession = create<SessionState>(() => ({
  role: currentRole,
  navAllowed: (route) => ROLES[currentRole()].nav.includes(route),
  can: (key) => Boolean(ROLES[currentRole()].can[key]),
}));

/** 组件里订阅角色变化用这个（zustand 的 selector 需要一个会变的值）。 */
export function useRole(): RoleCode {
  const r = useAuth((s) => s.user?.role);
  return isRole(r) ? r : FALLBACK;
}

/** 当前登录者的显示名。留痕由服务端从令牌取，这里只用于界面显示。 */
export function usePerson(): string {
  return useAuth((s) => s.user?.display_name) ?? ROLES[FALLBACK].person;
}

export const roleInfo = (r: RoleCode) => ROLES[r];
